import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotify } from '../../context/NotificationContext';
import { Table, Button, Input, Select, Space, Tag, Popconfirm, Tooltip, Avatar, Typography, Empty, Switch } from 'antd';
import { EditOutlined, CopyOutlined, DeleteOutlined, ReloadOutlined, StarFilled } from '@ant-design/icons';
import collectionsService from '../../services/collectionsService';

const { Search } = Input;
const { Option } = Select;
const { Text } = Typography;

const FavoriteCollectionsList = ({ activeTab }) => {
  const navigate = useNavigate();
  const notify = useNotify();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} favorites`,
  });
  const [filters, setFilters] = useState({ status: 'all', search: '' });

  // Handler function for toggling active status
  const handleToggleActive = async (collectionId, isActive) => {
    try {
      await collectionsService.updateCollection(collectionId, { is_active: isActive });
      setRows(prevRows => 
        prevRows.map(row => 
          row.collection_id === collectionId 
            ? { ...row, is_active: isActive }
            : row
        )
      );
      notify.success({ message: `Collection ${isActive ? 'activated' : 'deactivated'}` });
    } catch (e) {
      notify.error({ message: 'Failed to update status' });
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Collection',
      key: 'collection',
      render: (_, record) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative' }}>
            <Avatar
              size={40}
              src={record.image}
              style={{ 
                backgroundColor: '#fff7e6',
                border: '2px solid #faad14'
              }}
            >
              {record.name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <StarFilled 
              style={{ 
                position: 'absolute',
                top: -4,
                right: -4,
                color: '#faad14',
                fontSize: '12px',
                background: '#fff',
                borderRadius: '50%',
                padding: '1px'
              }} 
            />
          </div>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 500, color: '#262626', marginBottom: 2 }}>
              {record.name}
            </div>
            <div style={{ fontSize: '12px', color: '#8c8c8c' }}>
              {record.slug}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: 'Template',
      key: 'template',
      render: (_, record) => (
        <Text style={{ fontSize: '13px' }}>
          {record.template_relation?.temp_name || '-'}
        </Text>
      ),
      width: 150,
    },
    {
      title: 'Category',
      key: 'category',
      render: (_, record) => (
        <Text style={{ fontSize: '13px' }}>
          {record.categories_relation?.vc_name || '-'}
        </Text>
      ),
      width: 150,
    },
    {
      title: 'Type',
      key: 'type',
      render: (_, record) => (
        <Tag 
          color={record.is_manual ? 'blue' : 'default'}
          style={{ fontSize: '11px', margin: 0 }}
        >
          {record.is_manual ? 'Manual' : 'Auto'}
        </Tag>
      ),
      width: 80,
      align: 'center',
    },
    {
      title: 'Status',
      key: 'status',
      render: (_, record) => (
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Switch
            checked={record.collection_active}
            onChange={(checked) => handleToggleActive(record.collection_id, checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
            style={{ width: 90 }}
          />
        </Space>
      ),
      width: 110,
      align: 'center',
    },
    {
      title: 'Actions',
      key: 'actions',
      fixed: 'right',
      width: 120,
      render: (_, record) => (
        <Space size={2}>
          <Tooltip title="Remove from favorites">
            <Button 
              icon={<StarFilled />} 
              size="small" 
              type="text"
              onClick={() => handleRemoveFromFavorites(record.collection_id)}
              style={{ color: '#faad14' }}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              type="text" 
              onClick={() => navigate(`/collections/edit/${record.collection_id}`)}
              style={{ color: '#1890ff' }}
            />
          </Tooltip>
          <Tooltip title="Duplicate">
            <Button 
              icon={<CopyOutlined />} 
              size="small" 
              type="text" 
              onClick={() => handleDuplicate(record.collection_id)}
              style={{ color: '#52c41a' }}
            />
          </Tooltip>
          <Popconfirm 
            title="Delete collection?" 
            onConfirm={() => handleDelete(record.collection_id)} 
            okText="Yes" 
            cancelText="No"
          >
            <Button 
              icon={<DeleteOutlined />} 
              size="small" 
              type="text" 
              danger
            />
          </Popconfirm>
        </Space>
      ),
    },
  ], [navigate]);

  const getData = async () => {
    try {
      setLoading(true);
      const res = await collectionsService.getAllPinnedCollections({
        page: pagination.current,
        limit: pagination.pageSize,
        status: filters.status,
        search: filters.search,
      });
      
      if (res?.data?.status) {
        setRows(res.data.data || []);
        const meta = res.data.meta || {};
        setPagination((p) => ({ ...p, total: meta.totalEntries || 0, current: meta.currentPage || 1 }));
      }
    } catch (e) {
      notify.error({ message: 'Failed to load favorite collections' });
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (id) => {
    try {
      await collectionsService.duplicateCollection(id);
      notify.success({ message: 'Collection duplicated' });
      getData();
    } catch (e) {
      notify.error({ message: 'Failed to duplicate' });
    }
  };

  const handleDelete = async (id) => {
    try {
      await collectionsService.deleteCollection(id);
      notify.success({ message: 'Collection deleted' });
      getData();
    } catch (e) {
      notify.error({ message: 'Failed to delete' });
    }
  };

  const handleRemoveFromFavorites = async (collectionId) => {
    try {
      await collectionsService.toggleUserPinned(collectionId);
      notify.success({ message: 'Removed from favorites' });
      getData(); // Refresh the list to remove the item
    } catch (e) {
      notify.error({ message: 'Failed to remove from favorites' });
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, filters.status, filters.search, activeTab]);

  const emptyState = (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={
        <div>
          <Text type="secondary">No favorite collections yet</Text>
          <br />
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Star collections from the "All Collections" tab to add them here
          </Text>
        </div>
      }
    />
  );

  return (
    <div>
      {/* Simple Header Controls */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Search
          placeholder="Search favorites..."
          allowClear
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          onSearch={() => getData()}
          style={{ width: 280 }}
        />
        <Select
          style={{ width: 140 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
        >
          <Option value="all">All Status</Option>
          <Option value="active">Active</Option>
          <Option value="inactive">Inactive</Option>
        </Select>
        <div style={{ flex: 1 }} />
        <Space align="center">
          <StarFilled style={{ color: '#faad14', fontSize: '14px' }} />
          <Text type="secondary" style={{ fontSize: '13px' }}>
            {pagination.total} favorites
          </Text>
        </Space>
        <Button 
          icon={<ReloadOutlined />} 
          onClick={getData}
          type="text"
          size="small"
        />
      </div>

      {/* Clean Favorites Table */}
      {rows.length === 0 && !loading ? (
        <div style={{ 
          backgroundColor: '#fff', 
          borderRadius: 8, 
          padding: 48, 
          textAlign: 'center',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          {emptyState}
        </div>
      ) : (
        <Table
          rowKey="collection_id"
          columns={columns}
          dataSource={rows}
          loading={loading}
          pagination={pagination}
          onChange={(pg) => setPagination((p) => ({ ...p, current: pg.current, pageSize: pg.pageSize }))}
          scroll={{ x: 800 }}
          size="middle"
          style={{ 
            backgroundColor: '#fff',
            borderRadius: 8,
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}
        />
      )}
    </div>
  );
};

export default FavoriteCollectionsList;
